import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate } from 'react-router-dom'
import {
  api,
  getThumbnailUrl,
  getAssetUrl,
  type ProjectAssetPreview,
  type ProjectListItem,
} from '@ponpon/api-client'
import { LoadingState } from '../components/LoadingState'
import { useAuth } from '../contexts/AuthContext'

/**
 * Synthesize the unified preview list from the legacy
 * ``character_previews`` / ``location_previews`` arrays. Used as a
 * fallback when the backend hasn't yet been updated to emit the
 * ``previews`` field (older deploys). Newer deploys ship the unified
 * list directly.
 */
function previewsFromLegacy(p: ProjectListItem): ProjectAssetPreview[] {
  const chars: ProjectAssetPreview[] = (p.character_previews ?? []).map((c) => ({
    kind: 'character',
    name: c.name,
    path: c.image_path,
    node_type: 'character_designer',
  }))
  const locs: ProjectAssetPreview[] = (p.location_previews ?? []).map((l) => ({
    kind: 'location',
    name: l.name,
    path: l.image_path,
    node_type: 'location_designer',
  }))
  return [...chars, ...locs]
}

/**
 * Resolve the full preview list for a project. Prefers the backend's
 * unified ``previews`` field; falls back to the legacy
 * character/location arrays so old API deploys keep rendering.
 */
function projectPreviews(p: ProjectListItem): ProjectAssetPreview[] {
  return p.previews && p.previews.length > 0 ? p.previews : previewsFromLegacy(p)
}

function projectCover(p: ProjectListItem): ProjectAssetPreview | null {
  const all = projectPreviews(p)
  return all.length > 0 ? all[0] : null
}

/**
 * Pick the chip-strip entries — small thumbnails shown below the
 * metadata row. Video and storyboard kinds are poster-sized assets
 * (already represented by the cover); for chips we want the small
 * per-cast / per-location / per-image thumbnails. Falls back to
 * everything when no image/character/location entries exist so the
 * strip is never empty if there's *any* asset to show.
 */
function projectChips(p: ProjectListItem): ProjectAssetPreview[] {
  const all = projectPreviews(p)
  const thumb = all.filter((a) => a.kind !== 'video' && a.kind !== 'storyboard')
  return (thumb.length > 0 ? thumb : all).slice(0, 4)
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso)
    const now = Date.now()
    const diff = (now - d.getTime()) / 1000
    if (diff < 60)      return 'just now'
    if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`
    if (diff < 86400*7) return `${Math.floor(diff / 86400)}d ago`
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch { return iso.slice(0, 10) }
}

export function DashboardPage() {
  const { session, loading } = useAuth()
  const queryClient = useQueryClient()
  // Confirmation modal state — null when no project is queued for delete.
  // Lives at the page level (not the card) so the modal isn't trapped
  // inside a card's transform/overflow context.
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const deleteProject = useMutation({
    mutationFn: (projectId: string) => api.deleteProject(projectId),
    onSuccess: () => {
      // Invalidate the list query so the card disappears immediately.
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setDeleteConfirm(null)
      setDeleteError(null)
    },
    onError: (err: unknown) => {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete project')
    },
  })

  // IMPORTANT: every hook MUST be called in the same order on every
  // render. Don't add early returns above this line. The ``enabled``
  // flag gates the actual fetch until we have a session — the hook
  // itself still runs every render so the hook-count invariant holds.
  // See react.dev/reference/rules/rules-of-hooks.
  //
  // queryKey only carries the user id (no 'anon' sentinel) — when
  // session is null the key is ``['projects', undefined]`` and
  // react-query never fetches it, so we don't accumulate a useless
  // cache entry between login states.
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', session?.user.id],
    queryFn: async () => (await api.listProjects(50, 0)).data,
    enabled: !!session?.user.id,
    staleTime: 30_000,
  })

  if (loading) return <LoadingPad />
  if (!session) return <Navigate to="/login" replace />

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24">
      {/* Header — confident, modern */}
      <div className="flex items-end justify-between gap-6 mb-12">
        <div>
          <div className="pill mb-3">
            <span className="live-dot" />
            <span>Workspace · {data?.length ?? 0}</span>
          </div>
          <h1 className="h-display text-[clamp(2.4rem,5.4vw,4.6rem)] text-text">
            Your <span className="text-accent">productions</span>.
          </h1>
        </div>
        <Link to="/" className="btn-accent shrink-0">
          + New
        </Link>
      </div>

      {isLoading && (
        <div className="animate-pulse grid grid-cols-1 md:grid-cols-3 gap-4 py-6">
          <div className="h-44 rounded-lg bg-white/[0.05]" />
          <div className="h-44 rounded-lg bg-white/[0.05]" />
          <div className="h-44 rounded-lg bg-white/[0.05] hidden md:block" />
        </div>
      )}
      {error && (
        <div className="text-err py-6 text-[0.92rem]">
          ⨯ Failed to load: {String(error)}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="card p-16 text-center">
          <div className="pill mx-auto mb-4">
            <span>Empty workspace</span>
          </div>
          <h2 className="h-display text-[2.2rem] text-text-2 leading-tight">
            Nothing rolled yet.
          </h2>
          <p className="h-italic text-text-3 mt-2 mb-8 text-[1.15rem]">
            Write a sentence to start your first production.
          </p>
          <Link to="/" className="btn-accent">Open composer →</Link>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.map((p) => (
            <ProductionCard
              key={p.id}
              project={p}
              fmtDate={fmtDate}
              onDeleteClick={() => setDeleteConfirm({ id: p.id, name: p.name })}
            />
          ))}
        </div>
      )}

      {deleteConfirm && (
        <DeleteConfirmModal
          project={deleteConfirm}
          isPending={deleteProject.isPending}
          error={deleteError}
          onCancel={() => {
            if (deleteProject.isPending) return
            setDeleteConfirm(null)
            setDeleteError(null)
          }}
          onConfirm={() => deleteProject.mutate(deleteConfirm.id)}
        />
      )}
    </section>
  )
}

function ProductionCard({
  project,
  fmtDate,
  onDeleteClick,
}: {
  project: ProjectListItem
  fmtDate: (s: string) => string
  onDeleteClick?: () => void
}) {
  const cover = projectCover(project)
  const chips = projectChips(project)
  const totalThumbs = projectPreviews(project).filter(
    (a) => a.kind !== 'video' && a.kind !== 'storyboard',
  ).length

  return (
    <Link
      to={`/project/${project.id}/workspace`}
      className="card card-hover group flex flex-col"
      data-testid={`production-card-${project.id}`}
    >
      {/* Cover */}
      <div className="relative aspect-[4/3] bg-bg-2 overflow-hidden">
        <CoverArt cover={cover} project={project} />
        {/* Top-left status pill */}
        <div className="absolute top-3 left-3">
          <StatusPill project={project} />
        </div>
        {/* Top-right cluster: kind chip + delete button. Delete is
            hover-visible only so the card chrome stays clean. */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {cover && (cover.kind === 'video' || cover.kind === 'storyboard') && (
            <span
              className="pill"
              style={{ backdropFilter: 'blur(8px)' }}
              data-testid="cover-kind-pill"
            >
              {cover.kind === 'video' ? '▶ video' : '⊞ storyboard'}
            </span>
          )}
          {onDeleteClick && (
            <button
              type="button"
              onClick={(e) => {
                // Stop the click from propagating to the parent <Link>
                // (which would navigate into the workspace) and from
                // submitting any form (defensive).
                e.preventDefault()
                e.stopPropagation()
                onDeleteClick()
              }}
              data-testid={`delete-project-${project.id}`}
              title="Delete project"
              aria-label={`Delete project ${project.name}`}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1.5 rounded-md border border-line-2 bg-bg/70 hover:bg-err/20 hover:border-err/50 hover:text-err text-text-3"
              style={{ backdropFilter: 'blur(8px)' }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
        {/* Bottom gradient */}
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-bg via-bg/60 to-transparent pointer-events-none" />
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col">
        <h2
          className="text-[1.22rem] text-text leading-tight truncate"
          style={{ fontVariationSettings: '"wdth" 96, "opsz" 96', fontWeight: 600, letterSpacing: '-0.02em' }}
        >
          {project.name}
        </h2>

        <div className="mt-2 flex items-center gap-2 text-[0.82rem] text-text-3">
          <span>{fmtDate(project.updated_at)}</span>
          <span className="text-text-4">·</span>
          <span>{project.character_count} cast</span>
          <span className="text-text-4">·</span>
          <span>{project.chapter_count} ch</span>
        </div>

        {/* One-line story pitch — the user's main project context anchor. */}
        {project.logline && (
          <p
            className="mt-2 text-[0.86rem] text-text-2 leading-snug line-clamp-2"
            data-testid="card-logline"
            title={project.logline}
          >
            {project.logline}
          </p>
        )}

        {/* Cast chips (thumbnails of characters/locations/images) — driven
            from the unified preview list so video / storyboard projects
            also surface what's inside, not just the cover. */}
        {chips.length > 0 && (
          <div className="mt-4 flex -space-x-2" data-testid="card-chip-strip">
            {chips.map((p, i) => {
              const u = getThumbnailUrl(project.id, p.path, 80, project.updated_at)
              return (
                <div
                  key={`${p.node_type}-${p.path}-${i}`}
                  className="relative w-8 h-8 rounded-full overflow-hidden border-2 border-surface ring-1 ring-line-2 bg-bg-2"
                  title={p.name || p.node_type}
                  data-chip-kind={p.kind}
                  style={{ zIndex: chips.length - i }}
                >
                  {u && <img src={u} alt={p.name || p.node_type} className="w-full h-full object-cover" />}
                </div>
              )
            })}
            {totalThumbs > chips.length && (
              <div className="relative w-8 h-8 rounded-full border-2 border-surface ring-1 ring-line-2 bg-bg-2 flex items-center justify-center text-[0.7rem] text-text-3">
                +{totalThumbs - chips.length}
              </div>
            )}
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-line flex items-center justify-between text-[0.82rem]">
          <span className="text-text-3">Open in canvas</span>
          <span className="text-accent group-hover:translate-x-1 transition-transform">→</span>
        </div>
      </div>
    </Link>
  )
}

/**
 * First-letter italic watermark — the cover fallback when there's no
 * visual asset (or the asset URL can't be resolved). Centralized so a
 * future styling change lands in one place.
 */
function LetterWatermark({ name }: { name: string }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      data-testid="cover-letter"
    >
      <div
        className="text-[5rem] text-text-4 leading-none"
        style={{ fontFamily: 'var(--font-italic)', fontStyle: 'italic' }}
      >
        {name.slice(0, 1)}
      </div>
    </div>
  )
}

/**
 * Cover-art renderer for the production card. Picks the rendering
 * strategy by ``cover.kind``:
 *
 *   * ``video`` → muted, looping, auto-playing ``<video>``. Browsers
 *     pause off-screen tabs automatically. Plays raw MP4 via getAssetUrl
 *     with ``width=0`` — falsy, so the asset proxy returns the unresized
 *     stream (see api-client/client.ts: getAssetUrl skips the ``w`` query
 *     param when width is 0). DO NOT pass a non-zero width here — the
 *     thumbnail service can't transcode video.
 *   * everything else → ``<img>`` via the existing thumbnail service.
 *   * null cover OR unresolvable URL → italic first-letter watermark.
 */
function CoverArt({
  cover,
  project,
}: {
  cover: ProjectAssetPreview | null
  project: ProjectListItem
}) {
  if (!cover) return <LetterWatermark name={project.name} />

  if (cover.kind === 'video') {
    const src = getAssetUrl(project.id, cover.path, 0, project.updated_at)
    if (!src) return <LetterWatermark name={project.name} />
    return (
      <video
        src={src}
        muted
        loop
        autoPlay
        playsInline
        preload="metadata"
        data-testid="cover-video"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
      />
    )
  }

  // image / storyboard / character / location → use the thumbnail service.
  const src = getThumbnailUrl(project.id, cover.path, 800, project.updated_at)
  if (!src) return <LetterWatermark name={project.name} />
  return (
    <img
      src={src}
      alt={cover.name || project.name}
      data-testid="cover-image"
      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
    />
  )
}

/**
 * Confirmation modal shown before a project delete is committed.
 *
 * Lives at the page level (not the card) so the modal isn't trapped
 * inside a card's transform/overflow context. ESC + outside-click
 * dismiss are wired so users don't have to hit Cancel explicitly.
 *
 * While the mutation is in flight, both buttons disable and the
 * cancel path is gated to prevent dismissing mid-delete.
 */
function DeleteConfirmModal({
  project,
  isPending,
  error,
  onCancel,
  onConfirm,
}: {
  project: { id: string; name: string }
  isPending: boolean
  error: string | null
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onCancel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel()
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-confirm-title"
      data-testid="delete-confirm-modal"
    >
      <div
        className="card max-w-md mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4 mb-5">
          <div className="shrink-0 p-2.5 rounded-md border border-err/30 bg-err/10">
            <svg
              className="w-5 h-5 text-err"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3
              id="delete-confirm-title"
              className="text-text text-[1.1rem]"
              style={{ fontWeight: 600, letterSpacing: '-0.01em' }}
            >
              Delete this project?
            </h3>
            <p className="mt-2 text-[0.92rem] text-text-2 leading-snug">
              <span className="text-text">&ldquo;{project.name}&rdquo;</span> will be permanently deleted.
              This includes all chapters, characters, and generated assets.
              This action cannot be undone.
            </p>
            {error && (
              <p
                className="mt-3 text-[0.86rem] text-err"
                data-testid="delete-confirm-error"
              >
                {error}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            data-testid="delete-confirm-cancel"
            className="btn-quiet"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="delete-confirm-submit"
            className="px-4 py-2 rounded-md border border-err/60 bg-err/15 text-err hover:bg-err/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ fontSize: '0.9rem', fontWeight: 500 }}
          >
            {isPending ? 'Deleting…' : 'Delete project'}
          </button>
        </div>
      </div>
    </div>
  )
}

function StatusPill({ project }: { project: ProjectListItem }) {
  if (project.status === 'completed') {
    return <div className="pill"><span>shipped</span></div>
  }
  if (!project.story_expanded || project.status === 'draft') {
    return (
      <div className="pill pill--coral">
        <span className="live-dot live-dot--coral" />
        <span>draft</span>
      </div>
    )
  }
  return (
    <div className="pill pill--accent">
      <span className="live-dot" />
      <span>in production</span>
    </div>
  )
}

function LoadingPad() {
  return <LoadingState label="Loading workspace" />
}
